import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem('gamma_token');
        delete apiClient.defaults.headers.common['Authorization'];
        setUser(null);
    }, []);

    const checkUser = useCallback(async () => {
        try {
            const response = await apiClient.get('/auth/me');
            setUser(response.data);
        } catch (error) {
            console.error("Auth check failed", error);
            logout();
        } finally {
            setLoading(false);
        }
    }, [logout]);

    const extractWhopToken = useCallback(() => {
        // AUTOMATIC LOCAL BYPASS
        const isLocal = window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname.startsWith('192.168.');

        const searchParams = new URLSearchParams(window.location.search);
        const whopToken = searchParams.get('whop_token') || searchParams.get('token') || searchParams.get('access_token');

        // If we are local and don't have a REAL whop token in URL/Storage, force the bypass
        const existingToken = localStorage.getItem('gamma_token');
        const hasRealToken = (whopToken && whopToken !== 'local-bypass-token') ||
            (existingToken && existingToken !== 'local-bypass-token');

        if (isLocal && !hasRealToken) {
            const token = 'local-bypass-token';
            localStorage.setItem('gamma_token', token);
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            apiClient.defaults.headers.common['x-whop-user-token'] = token;
            return token;
        }

        if (whopToken) {
            localStorage.setItem('gamma_token', whopToken);
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${whopToken}`;
            apiClient.defaults.headers.common['x-whop-user-token'] = whopToken;
            return whopToken;
        }

        if (existingToken) {
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${existingToken}`;
            apiClient.defaults.headers.common['x-whop-user-token'] = existingToken;
            return existingToken;
        }
        return null;
    }, []);

    useEffect(() => {
        const token = extractWhopToken();
        if (token) {
            checkUser();
        } else {
            setLoading(false);
        }
    }, [checkUser, extractWhopToken]);

    const login = (token, userData) => {
        localStorage.setItem('gamma_token', token);
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, checkUser, extractWhopToken }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
